import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMyOrderDetailQuery } from '@/entities/order';
import { useMyOrderInquiriesQuery } from '@/entities/order-inquiry';
import { OrderInquiryForm, useCreateOrderInquiry } from '@/features/order-inquiry/write';
import { myOrderDetailPath } from '@/shared/config';
import { EyebrowLabel, PageHeading, SitePageShell } from '@/shared/ui';
import { OrderInquiryPageNotice } from '@/widgets/order-inquiry-page-notice';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/** 교환·환불 문의 접수(/my/orders/:orderId/inquiry, 문의 설계 D12) — 사진 업로드로 화면이 길어 별도 화면. 접수하면 내 주문 상세로 돌아가 내역에 보인다. */
export const OrderInquiryWritePage = (): ReactElement => {
  const { orderId = '' } = useParams();
  const parsed = normalizedUuidSchema.safeParse(orderId);
  const id = parsed.success ? parsed.data : '';
  const navigate = useNavigate();
  const order = useMyOrderDetailQuery(id, { enabled: parsed.success });
  const inquiries = useMyOrderInquiriesQuery(id, { enabled: parsed.success });
  const create = useCreateOrderInquiry();
  const writable = inquiries.data?.writable;
  const back = { to: myOrderDetailPath(id), label: '내 주문 상세로' };

  const body = ((): ReactElement => {
    if (!parsed.success) {
      return <OrderInquiryPageNotice message="문의를 남길 수 없는 주문이에요." />;
    }
    if (inquiries.isPending) {
      return (
        <p role="status" className="text-sm text-muted-foreground">
          주문을 확인하는 중이에요
        </p>
      );
    }
    if (inquiries.isError) {
      return (
        <OrderInquiryPageNotice
          message="주문을 확인하지 못했어요. 잠시 후 다시 시도해 주세요."
          onRetry={() => void inquiries.refetch()}
        />
      );
    }
    if (writable === 'NOT_STARTED') {
      return (
        <OrderInquiryPageNotice message="제작 착수 전에는 취소 요청을 이용해 주세요." link={back} />
      );
    }
    if (writable === 'ALREADY_OPEN') {
      return (
        <OrderInquiryPageNotice
          message="처리 중인 문의가 있어요. 답변 뒤에 다시 접수할 수 있어요."
          link={back}
        />
      );
    }
    if (writable !== 'WRITABLE') {
      return <OrderInquiryPageNotice message="문의를 남길 수 없는 주문이에요." link={back} />;
    }
    return (
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-16">
        <aside
          aria-label="문의할 주문"
          className="rounded-2xl bg-muted/60 p-5 lg:sticky lg:top-28 lg:order-2"
        >
          <p className="text-xs text-muted-foreground">문의할 주문</p>
          <p className="text-lg font-semibold">{order.data?.productName ?? '주문한 상품'}</p>
          {order.data ? (
            <p className="text-sm text-muted-foreground">
              주문 {order.data.orderNumber} · {order.data.quantity.toLocaleString('ko-KR')}개
            </p>
          ) : null}
        </aside>
        <OrderInquiryForm
          submitLabel="접수"
          onSubmit={async (values) => {
            await create.create({ orderId: id, ...values, imageIds: [...values.imageIds] });
            navigate(myOrderDetailPath(id));
          }}
        />
      </div>
    );
  })();

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <div className="flex flex-col gap-4 border-b border-foreground/10 pb-8">
          <EyebrowLabel>Exchange · Refund</EyebrowLabel>
          <PageHeading
            title="교환·환불 문의"
            description="받아 보신 상품에 문제가 있으면 사진과 함께 알려 주세요. 운영자가 확인한 뒤 답변을 메일로 보내 드려요."
          />
        </div>
        {body}
      </div>
    </SitePageShell>
  );
};
